<template>
  <div>
    <MainLayout>
      <div v-if="loaded && teammate" class="flex justify-between items-start gap-4">
        <div class="lg:w-2/3">
          <Card>
            <CardContent>
              <ReviewForm :teammate="teammate" :files="files" @created="onCreated" />
            </CardContent>
          </Card>
        </div>

        <div class="lg:w-1/3">
          <EventRecordingCard :event-recording="eventRecording" />
          <TeammateInfoCard :teammate="teammate">
            <router-link
              v-if="previousPipelineReview"
              target="_blank"
              :to="`/team/${teammate.id}/pipeline_reviews/${previousPipelineReview.id}`"
              class="sidebar-link mt-12"
              >View previous pipeline review</router-link
            >
          </TeammateInfoCard>
          <Card>
            <CardContent>
              <h3 class="text-lg leading-6 font-medium text-gray-900">Attachments</h3>
              <FileInput
                limits="any file"
                :multiple="true"
                :model-value="files"
                accept="*"
                @update:model-value="files = $event"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import { Card, CardContent } from '@/components/ui/card';
import ReviewForm from '@/components/shared/pipeline_reviews/ReviewForm.vue';
import EventRecordingCard from './EventRecordingCard.vue';
import TeammateInfoCard from './TeammateInfoCard.vue';
import { Api } from '@/api';
import type { EventRecordingEntity } from '@/api/entity_types/EventRecording';
import type { PipelineReviewListResponse } from '@/api/pipeline_reviews';

const route = useRoute();
const router = useRouter();

const contactId = Number(route.params.contactId);

const loaded = ref(false);
const teammate = ref<{ id: number; name: string; email: string; phone_number: string } | null>(null);
const eventRecording = ref<EventRecordingEntity | null>(null);
const pipelineReviews = ref<PipelineReviewListResponse[]>([]);
const files = ref<File[]>([]);

const previousPipelineReview = computed(() => pipelineReviews.value[0]);

const onCreated = (reviewId: number) => {
  router.push(`/team/${contactId}/pipeline_reviews/${reviewId}`);
};

onMounted(async () => {
  const [contactData, reviewsData, contactEventData] = await Promise.all([
    Api.Contacts.getContact(String(contactId)),
    Api.PipelineReviews.listPipelineReviews(contactId),
    Api.PipelineReviews.getContactEvent(contactId),
  ]);

  teammate.value = contactData?.record;
  if (reviewsData.status === 'success') {
    pipelineReviews.value = reviewsData.data;
  }
  if (contactEventData.status === 'success') {
    eventRecording.value = contactEventData.data?.event_recording ?? null;
  }
  loaded.value = true;
});
</script>

<style scoped>
@reference "tailwindcss";

.sidebar-link {
  @apply flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500;
}
</style>
